// ============================================================
// Activity Tracker - Ziyaretçi aktivitelerini localStorage'a kaydet
// IP (ipify.org), sayfa URL, zaman, user agent, session ID
// ============================================================

export interface ActivityLog {
  id: string;
  ip: string;
  page: string;
  pageTitle: string;
  userAgent: string;
  timestamp: string;
  referrer: string;
  sessionId: string;
  country?: string;
}

const STORAGE_KEY = 'visitor_activity_logs';
const MAX_LOGS = 2000;
const IP_CACHE_KEY = 'visitor_ip_cache';
const SESSION_KEY = 'visitor_session_id';

// IP'yi önbelleğe al (sessionStorage)
let ipMemory: string | null = null;

export async function getClientIp(): Promise<string> {
  if (ipMemory) return ipMemory;

  // sessionStorage önbelleği
  try {
    const cached = sessionStorage.getItem(IP_CACHE_KEY);
    if (cached) {
      ipMemory = cached;
      return cached;
    }
  } catch {}

  // ipify'dan al
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    const res = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    clearTimeout(timeout);
    const data = await res.json();
    const ip = data.ip || 'bilinmiyor';
    ipMemory = ip;
    try { sessionStorage.setItem(IP_CACHE_KEY, ip); } catch {}
    return ip;
  } catch {
    ipMemory = 'bilinmiyor';
    return 'bilinmiyor';
  }
}

export function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return 'session-error';
  }
}

export async function trackPageView(path: string, title?: string): Promise<void> {
  try {
    const ip = await getClientIp();
    const sessionId = getSessionId();

    const log: ActivityLog = {
      id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
      ip,
      page: path,
      pageTitle: title || document.title || path,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      referrer: document.referrer || '',
      sessionId,
    };

    const existing: ActivityLog[] = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || '[]'
    );
    existing.unshift(log);
    // Max 2000 log sakla
    if (existing.length > MAX_LOGS) existing.splice(MAX_LOGS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // tracker hiçbir zaman crash'e yol açmasın
  }
}

export function getLogs(): ActivityLog[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

export function clearLogs(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/** İnsan okunabilir sayfa adı */
export function pageLabel(path: string): string {
  const map: Record<string, string> = {
    '/': 'Anasayfa',
    '/sepet': 'Sepet',
    '/odeme': 'Ödeme',
    '/favoriler': 'Favoriler',
    '/arama': 'Arama',
    '/blog': 'Blog',
    '/hakkimizda': 'Hakkımızda',
    '/iletisim': 'İletişim',
    '/sss': 'SSS',
    '/gizlilik-politikasi': 'Gizlilik',
    '/cerez-politikasi': 'Çerez',
    '/kullanim-kosullari': 'Kullanım Koşulları',
    '/kampanyalar': 'Kampanyalar',
    '/giris': 'Giriş',
  };
  if (map[path]) return map[path];
  if (path.startsWith('/kategori/')) return 'Kategori: ' + path.split('/')[2];
  if (path.startsWith('/marka/')) return 'Marka: ' + path.split('/')[2];
  if (path.startsWith('/urun/')) return 'Ürün: ' + path.split('/')[2];
  if (path.startsWith('/blog/')) return 'Blog: ' + path.split('/')[2];
  if (path.startsWith('/admin')) return 'Admin Panel';
  return path;
}

/** UA'dan tarayıcı adı */
export function getBrowser(ua: string): string {
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('OPR') || ua.includes('Opera')) return 'Opera';
  return 'Diğer';
}

/** UA'dan işletim sistemi */
export function getOS(ua: string): string {
  if (ua.includes('Windows NT')) return 'Windows';
  if (ua.includes('Mac OS X') || ua.includes('Macintosh')) return 'macOS';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Linux')) return 'Linux';
  return 'Diğer';
}
